// CuadreDeCajaReport.jsx
import * as React from "react";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  alpha,
  Fade,
  Card,
  Chip
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import { DataGrid } from "@mui/x-data-grid";
import AppNavbar from "../../../../components/AppNavbar";
import Header from "../../../../components/Header";
import SideMenu from "../../../../components/SideMenu";
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../../../dashboard/theme/customizations';
import AppTheme from "../../../../shared-theme/AppTheme";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { formatNumberWithCommas } from "../../../../utils/generalFunctions";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { tiposDeInteres } from "../../../../utils/staticvariables";
import AlertMessageDialog from '../../../../components/Dialogs/alertMessageDialog';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const MetricCard = ({ label, value, icon, delay = 0, subtitle }) => (
  <Fade in timeout={800 + delay}>
    <Card sx={{ 
      p: 3, 
      minWidth: 280, 
      flex: 1,
      background: 'linear-gradient(135deg, #18586b 0%, #0f3a42 100%)',
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-8px) scale(1.02)',
        boxShadow: '0 20px 40px rgba(24, 88, 107, 0.3)',
        '& .metric-icon': {
          transform: 'scale(1.1) rotate(5deg)',
        },
        '& .metric-value': {
          transform: 'scale(1.05)',
        }
      }
    }}>
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ opacity: 0.9 }}>
            {label}
          </Typography>
          {icon && (
            <Box className="metric-icon" sx={{ 
              p: 1.5, 
              borderRadius: '50%', 
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              {icon}
            </Box>
          )}
        </Box>
        <Typography className="metric-value" variant="h4" fontWeight={800} sx={{ 
          mb: 1,
          transition: 'all 0.3s ease',
          background: 'linear-gradient(45deg, #ffffff, #e0f2f1)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.875rem' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      
      {/* Animated background elements */}
      <Box sx={{
        position: 'absolute',
        top: -20,
        right: -20,
        width: 80,
        height: 80,
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.1)',
        zIndex: 1,
        animation: 'float 6s ease-in-out infinite'
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: -30,
        left: -30,
        width: 60,
        height: 60,
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.05)',
        zIndex: 1,
        animation: 'float 4s ease-in-out infinite reverse'
      }} />
      
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(180deg); }
        }
      `}</style>
    </Card>
  </Fade>
);

export default function CuadreDeCajaReport(props) {
  const navigate = useNavigate();
  
  // ---- Filters (controlled state) ----
  const [branch, setBranch] = React.useState("");
  const [user, setUser] = React.useState("");
  const [paymentMethod, setPaymentMethod] = React.useState("");
  const [category, setCategory] = React.useState("");
  const [interestType, setInterestType] = React.useState("");
  const [dateFrom, setDateFrom] = React.useState(dayjs().startOf('day'));
  const [dateTo, setDateTo] = React.useState(dayjs().endOf('day'));
  
  // ---- Data state ----
  const [rows, setRows] = React.useState([]);
  const [filteredRows, setFilteredRows] = React.useState([]);
  const [isLoadingData, setIsLoadingData] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  // ---- Alert dialog state ----
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertConfig, setAlertConfig] = React.useState({
    message: '',
    title: '',
    severity: 'success'
  });

  // Transaction categories
  const transactionCategories = [
    { value: 'payment', label: 'Pagos de Préstamos', color: 'success' },
    { value: 'disbursement', label: 'Desembolsos', color: 'error' },
    { value: 'inicial', label: 'Cobros de Inicial', color: 'info' },
    { value: 'gasto', label: 'Gastos Operativos', color: 'warning' }
  ];

  // ---- Extract unique values for filter options ----
  const filterOptions = React.useMemo(() => {
    if (rows.length === 0) {
      return {
        branches: [],
        users: [],
        paymentMethods: [],
        interestTypes: []
      };
    }

    const branches = [...new Set(rows.map(row => row.branch).filter(Boolean))].sort();
    const users = [...new Set(rows.map(row => row.user).filter(Boolean))].sort();
    const paymentMethods = [...new Set(rows.map(row => row.paymentMethod).filter(Boolean))].sort();
    const interestTypes = [...new Set(rows.map(row => row.interestType).filter(Boolean))].sort();

    return {
      branches,
      users,
      paymentMethods,
      interestTypes
    };
  }, [rows]);

  const currency = (n) => {
    if (n === undefined || n === null || isNaN(n)) return 'RD$0.00';
    return `RD$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Alert helpers
  const showErrorAlert = (title, message) => {
    setAlertConfig({
      title,
      message,
      severity: 'error'
    });
    setAlertOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
  };

  const columns = [
    { 
      field: "date", 
      headerName: "Fecha", 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        return <Typography variant="body2" mt={2}>{params.row.date}</Typography>
      }
    },
    { 
      field: "transactionType", 
      headerName: "Tipo", 
      flex: 1, 
      minWidth: 160,
      renderCell: (params) => {
        const categoryData = transactionCategories.find(cat => cat.value === params.row.transactionType);
        return (
          <Chip 
            label={categoryData?.label || params.row.transactionType} 
            color={categoryData?.color || 'default'}
            size="small"
            sx={{ mt: 1 }}
          />
        );
      }
    },
    { 
      field: "client", 
      headerName: "Cliente", 
      flex: 1, 
      minWidth: 180 
    },
    { 
      field: "capitalPagado", 
      headerName: "Capital", 
      minWidth: 130,
      renderCell: (params) => {
        return params.row.capitalPagado > 0 ? (
          <Typography variant="body2" mt={2} color="success.main" fontWeight={600}>
            {formatNumberWithCommas(params.row.capitalPagado.toFixed(2))}
          </Typography>
        ) : (
          <Typography variant="body2" mt={2} color="text.disabled">-</Typography>
        );
      }
    },
    { 
      field: "interestPagado", 
      headerName: "Interés", 
      minWidth: 130,
      renderCell: (params) => {
        return params.row.interestPagado > 0 ? (
          <Typography variant="body2" mt={2} color="success.main" fontWeight={600}>
            {formatNumberWithCommas(params.row.interestPagado.toFixed(2))}
          </Typography>
        ) : (
          <Typography variant="body2" mt={2} color="text.disabled">-</Typography>
        );
      }
    },
    {
      field: "moraPagado",
      headerName: "Mora",
      minWidth: 130,
      renderCell: (params) => {
        return params.row.moraPagado > 0 ? (
          <Typography variant="body2" mt={2} color="success.main" fontWeight={600}>
            {formatNumberWithCommas(params.row.moraPagado.toFixed(2))}
          </Typography>
        ) : (
          <Typography variant="body2" mt={2} color="text.disabled">-</Typography>
        );
      }
    },
    {
      field: "income",
      headerName: "Ingreso",
      minWidth: 130,
      renderCell: (params) => {
        return params.row.income > 0 ? (
          <Typography variant="body2" mt={2} color="success.main" fontWeight={600}>
            {formatNumberWithCommas(params.row.income.toFixed(2))}
          </Typography>
        ) : (
          <Typography variant="body2" mt={2} color="text.disabled">-</Typography>
        );
      }
    },
    {
      field: "expense",
      headerName: "Egreso",
      minWidth: 130,
      renderCell: (params) => {
        return params.row.expense > 0 ? (
          <Typography variant="body2" mt={2} color="error.main" fontWeight={600}>
            {formatNumberWithCommas(params.row.expense.toFixed(2))}
          </Typography>
        ) : (
          <Typography variant="body2" mt={2} color="text.disabled">-</Typography>
        );
      }
    },

    { 
      field: "paymentMethod", 
      headerName: "Método de Pago", 
      flex: 1, 
      minWidth: 150,
      renderCell: (params) => {
        const colorMap = {
          'EFECTIVO': 'success',
          'TRANSFERENCIA': 'info',
          'CHEQUE': 'warning',
          'TARJETA': 'secondary'
        };
        return (
          <Chip 
            label={params.row.paymentMethod} 
            color={colorMap[params.row.paymentMethod] || 'default'}
            size="small"
            sx={{ mt: 1 }}
          />
        );
      }
    },
    {
      field: "interestType",
      headerName: "Tipo de Interés",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        return <Typography variant="body2" mt={2}>{params.row.interestType || 'N/A'}</Typography>
      }
    },
    { 
      field: "user", 
      headerName: "Usuario", 
      flex: 1, 
      minWidth: 150 
    },
    { 
      field: "branch", 
      headerName: "Sucursal", 
      flex: 1, 
      minWidth: 150 
    }
  ];

  // ---- Calculate totals ----
  const totals = React.useMemo(() => {
    const totalIncome = filteredRows.reduce((acc, r) => acc + (parseFloat(r.income) || 0), 0);
    const totalExpense = filteredRows.reduce((acc, r) => acc + (parseFloat(r.expense) || 0), 0);
    const totalCapital = filteredRows.reduce((acc, r) => acc + (parseFloat(r.capitalPagado) || 0), 0);
    const totalInterest = filteredRows.reduce((acc, r) => acc + (parseFloat(r.interestPagado) || 0), 0);
    const totalMora = filteredRows.reduce((acc, r) => acc + (parseFloat(r.moraPagado) || 0), 0);
    const netBalance = totalIncome - totalExpense;
    const transactions = filteredRows.length;

    // Calculate by category
    const byCategory = transactionCategories.reduce((acc, cat) => {
      const categoryRows = filteredRows.filter(r => r.transactionType === cat.value);
      const categoryTotal = categoryRows.reduce((sum, r) => 
        sum + (parseFloat(r.income) || 0) + (parseFloat(r.expense) || 0), 0
      );
      acc[cat.value] = {
        count: categoryRows.length,
        total: categoryTotal,
        label: cat.label,
        color: cat.color
      };
      return acc;
    }, {});

    return {
      totalIncome,
      totalExpense,
      totalCapital,
      totalInterest,
      totalMora,
      netBalance,
      transactions,
      byCategory
    };
  }, [filteredRows]);

  // ---- Fetch data from API ----
  const fetchData = async () => {
    setIsLoadingData(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/prestamos/reporte/cuadredecaja?idcompany=${localStorage.getItem('idcompany')}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!Array.isArray(data)) {
        console.error('API response is not an array:', data);
        throw new Error('Invalid API response format');
      }

      // Transform API data to match frontend expectations
      
      const transformedData = data.map((item, index) => ({
        id: index + 1,
        transactionId: item.transaction_id || `TRX-${String(index + 1).padStart(6, '0')}`,
        date: item.date || dayjs().format('DD/MM/YYYY'),
        time: item.time || dayjs().format('HH:mm:ss'),
        client: item.client || 'N/A',
        concept: item.concept || 'Transacción',
        paymentMethod: item.payment_method.toUpperCase() || 'Efectivo',
        user: item.user || 'Usuario',
        branch: item.branch || 'Sucursal',
        income: parseFloat(item.income) || 0,
        expense: parseFloat(item.expense) || 0,
        balance: parseFloat(item.balance) || 0,
        transactionType: item.transaction_type || 'other',
        interestType: item.interest_type || null,
        capitalPagado: parseFloat(item.capitalPagado) || 0,
        interestPagado: parseFloat(item.interestPagado) || 0,
        moraPagado: parseFloat(item.moraPagado) || 0
      }));

      setRows(transformedData);
      setFilteredRows(transformedData);
      console.log('Cuadre de Caja data loaded successfully:', transformedData.length, 'transactions');
      
    } catch (error) {
      console.error('Error fetching cuadre de caja data:', error);
      showErrorAlert('Error al cargar reporte', `No se pudo cargar el cuadre de caja: ${error.message}`);
      setRows([]);
      setFilteredRows([]);
    } finally {
      setIsLoadingData(false);
    }
  };

  // ---- Filter data based on selected filters ----
  const handleGenerate = () => {
    setIsLoading(true);
    
    setTimeout(() => {
      let filtered = [...rows];

      // Filter by category (transaction type)
      if (category) {
        filtered = filtered.filter(row => row.transactionType === category);
      }

      // Filter by branch
      if (branch) {
        filtered = filtered.filter(row => row.branch === branch);
      }

      // Filter by user
      if (user) {
        filtered = filtered.filter(row => row.user === user);
      }

      // Filter by payment method
      if (paymentMethod) {
        filtered = filtered.filter(row => row.paymentMethod === paymentMethod);
      }

      // Filter by interest type
      if (interestType) {
        filtered = filtered.filter(row => row.interestType === interestType);
      }

      // Filter by date range
      if (dateFrom) {
        filtered = filtered.filter(row => {
          const rowDate = dayjs(row.date, 'DD/MM/YYYY');
          return rowDate.isAfter(dateFrom) || rowDate.isSame(dateFrom, 'day');
        });
      }
      if (dateTo) {
        filtered = filtered.filter(row => {
          const rowDate = dayjs(row.date, 'DD/MM/YYYY');
          return rowDate.isBefore(dateTo) || rowDate.isSame(dateTo, 'day');
        });
      }

      setFilteredRows(filtered);
      setIsLoading(false);
      console.log('Data filtered successfully:', filtered.length, 'transactions');
    }, 500);
  };

  const handleClearFilters = () => {
    setBranch("");
    setUser("");
    setPaymentMethod("");
    setCategory("");
    setInterestType("");
    setDateFrom(dayjs().startOf('day'));
    setDateTo(dayjs().endOf('day'));
    setFilteredRows(rows);
  };

  const handleExportPDF = () => {
    if (filteredRows.length === 0) {
      showErrorAlert('Sin datos', 'No hay datos para exportar. Por favor aplique filtros primero.');
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      let yPosition = 20;

      // Header - Company Logo and Title
      const companyName = localStorage.getItem('nombre_compania') || 'Empresa';
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Cuadre de Caja', 14, yPosition);
      
      yPosition += 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Empresa: ${companyName}`, 14, yPosition);
      
      yPosition += 5;
      const dateRange = `Período: ${dateFrom ? dateFrom.format('DD/MM/YYYY') : 'N/A'} - ${dateTo ? dateTo.format('DD/MM/YYYY') : 'N/A'}`;
      doc.text(dateRange, 14, yPosition);
      
      yPosition += 5;
      doc.text(`Generado: ${dayjs().format('DD/MM/YYYY HH:mm:ss')}`, 14, yPosition);
      
      yPosition += 8;

      // Filter Information
      const activeFilters = [];
      if (branch) activeFilters.push(`Sucursal: ${branch}`);
      if (user) activeFilters.push(`Usuario: ${user}`);
      if (paymentMethod) activeFilters.push(`Método: ${paymentMethod}`);
      if (category) {
        const catLabel = transactionCategories.find(c => c.value === category)?.label || category;
        activeFilters.push(`Categoría: ${catLabel}`);
      }
      if (interestType) {
        const tipoLabel = tiposDeInteres.find(t => t.value === interestType)?.label || interestType;
        activeFilters.push(`Tipo Interés: ${tipoLabel}`);
      }

      if (activeFilters.length > 0) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Filtros Aplicados:', 14, yPosition);
        yPosition += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        activeFilters.forEach(filter => {
          doc.text(`• ${filter}`, 18, yPosition);
          yPosition += 4;
        });
        yPosition += 3;
      }

      // Summary Metrics
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Resumen', 14, yPosition);
      yPosition += 6;

      const summaryData = [
        ['Total Capital', currency(totals.totalCapital)],
        ['Total Interés', currency(totals.totalInterest)],
        ['Total Mora', currency(totals.totalMora)],
        ['Total Ingresos', currency(totals.totalIncome)],
        ['Total Egresos', currency(totals.totalExpense)],
        ['Balance Neto', currency(totals.netBalance)],
        ['Total Transacciones', totals.transactions.toString()]
      ];

      autoTable(doc, {
        startY: yPosition,
        head: [['Concepto', 'Valor']],
        body: summaryData,
        theme: 'grid',
        headStyles: { fillColor: [24, 88, 107], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 60, fontStyle: 'bold' },
          1: { cellWidth: 40, halign: 'right' }
        },
        margin: { left: 14, right: 14 }
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Transactions Table
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Detalle de Transacciones', 14, yPosition);
      yPosition += 6;

      // Prepare table data
      const tableData = filteredRows.map(row => {
        const categoryLabel = transactionCategories.find(c => c.value === row.transactionType)?.label || row.transactionType;
        return [
          row.date || '',
          categoryLabel,
          row.client || '',
          row.concept || '',
          row.capitalPagado > 0 ? formatNumberWithCommas(row.capitalPagado.toFixed(2)) : '-',
          row.interestPagado > 0 ? formatNumberWithCommas(row.interestPagado.toFixed(2)) : '-',
          row.moraPagado > 0 ? formatNumberWithCommas(row.moraPagado.toFixed(2)) : '-',
          row.income > 0 ? formatNumberWithCommas(row.income.toFixed(2)) : '-',
          row.expense > 0 ? formatNumberWithCommas(row.expense.toFixed(2)) : '-',
          row.paymentMethod || '',
          row.user || '',
          row.branch || ''
        ];
      });

      autoTable(doc, {
        startY: yPosition,
        head: [['Fecha', 'Tipo', 'Cliente', 'Concepto', 'Capital', 'Interés', 'Mora', 'Ingreso', 'Egreso', 'Método Pago', 'Usuario', 'Sucursal']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [24, 88, 107], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        bodyStyles: { fontSize: 7 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 25 },
          2: { cellWidth: 28 },
          3: { cellWidth: 28 },
          4: { cellWidth: 18, halign: 'right' },
          5: { cellWidth: 18, halign: 'right' },
          6: { cellWidth: 18, halign: 'right' },
          7: { cellWidth: 20, halign: 'right' },
          8: { cellWidth: 20, halign: 'right' },
          9: { cellWidth: 22 },
          10: { cellWidth: 22 },
          11: { cellWidth: 22 }
        },
        margin: { left: 14, right: 14 },
        styles: { overflow: 'linebreak', cellPadding: 2 }
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Category Breakdown
      if (yPosition > 180) {
        doc.addPage();
        yPosition = 20;
      }

     

      // Footer
      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.text(
          `Página ${i} de ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }

      // Save PDF
      const pdfBlob = doc.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');


    } catch (error) {
      console.error('Error generating PDF:', error);
      showErrorAlert('Error al generar PDF', `No se pudo generar el PDF: ${error.message}`);
    }
  };

  const handleExportExcel = () => {
    console.log("Export Excel");
    showErrorAlert('Función en desarrollo', 'La funcionalidad de exportar a Excel estará disponible próximamente');
  };

  const handlePrint = () => {
    if (filteredRows.length === 0) {
      showErrorAlert('Sin datos', 'No hay datos para imprimir. Por favor aplique filtros primero.');
      return;
    }
    window.print();
  };

  // ---- Filter content ----
  const filterContent = (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Categoría
            </Typography>
            <FormControl fullWidth>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                displayEmpty
                sx={{ backgroundColor: 'white' }}
              >
                <MenuItem value="">Todas las Categorías</MenuItem>
                {transactionCategories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={cat.label} 
                        color={cat.color} 
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Sucursal
            </Typography>
            <FormControl fullWidth>
              <Select
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                displayEmpty
                sx={{ backgroundColor: 'white' }}
              >
                <MenuItem value="">Todas las Sucursales</MenuItem>
                {filterOptions.branches.map((branchOption) => (
                  <MenuItem key={branchOption} value={branchOption}>
                    {branchOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Usuario
            </Typography>
            <FormControl fullWidth>
              <Select
                value={user}
                onChange={(e) => setUser(e.target.value)}
                displayEmpty
                sx={{ backgroundColor: 'white' }}
              >
                <MenuItem value="">Todos los Usuarios</MenuItem>
                {filterOptions.users.map((userOption) => (
                  <MenuItem key={userOption} value={userOption}>
                    {userOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Método de Pago
            </Typography>
            <FormControl fullWidth>
              <Select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                displayEmpty
                sx={{ backgroundColor: 'white' }}
              >
                <MenuItem value="">Todos los Métodos</MenuItem>
                {filterOptions.paymentMethods.map((method) => (
                  <MenuItem key={method} value={method}>
                    {method}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Tipo de Interés
            </Typography>
            <FormControl fullWidth>
              <Select
                value={interestType}
                onChange={(e) => setInterestType(e.target.value)}
                displayEmpty
                sx={{ backgroundColor: 'white' }}
              >
                <MenuItem value="">Todos los Tipos</MenuItem>
                {tiposDeInteres.map((tipo) => (
                  <MenuItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Fecha Desde
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateFrom}
                format="DD/MM/YYYY"
                onChange={(newValue) => setDateFrom(newValue)}
                label="Seleccionar fecha"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { backgroundColor: 'white' }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Fecha Hasta
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateTo}
                format="DD/MM/YYYY"
                onChange={(newValue) => setDateTo(newValue)}
                label="Seleccionar fecha"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { backgroundColor: 'white' }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Button 
            variant="contained" 
            onClick={handleGenerate} 
            color="primarybutton" 
            disabled={isLoading}
            sx={{fontWeight: 600, minWidth: 120, mt: 3}}
            fullWidth
          >
            {isLoading ? 'Filtrando...' : 'Aplicar Filtros'}
          </Button>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Button 
            variant="outlined" 
            onClick={handleClearFilters} 
            disabled={isLoading}
            sx={{fontWeight: 600, minWidth: 120, mt: 3}}
            fullWidth
          >
            Limpiar Filtros
          </Button>
        </Grid>
      </Grid>

      {/* Export Options Row */}
      <Stack 
        direction={{ xs: "column", sm: "row" }} 
        spacing={2} 
        alignItems="center"
        sx={{ 
          borderTop: 1, 
          borderColor: 'divider', 
          pt: 2,
          justifyContent: 'space-between'
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button onClick={handleExportPDF} startIcon={<PictureAsPdfOutlinedIcon />} color="inherit" size="small">
            Exportar a PDF
          </Button>
          <Button onClick={handleExportExcel} startIcon={<GridOnOutlinedIcon />} color="inherit" size="small">
            Exportar a Excel
          </Button>
          <Button onClick={handlePrint} startIcon={<PrintOutlinedIcon />} color="inherit" size="small">
            Imprimir Reporte
          </Button>
        </Stack>
      </Stack>
    </Box>
  );

  React.useEffect(() => {
    fetchData();
  }, []);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        {/* Main content */}
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <Header parent={"Gestión Comercial"} child={"Reportes"} />
            <Container maxWidth={false} sx={{ px: 5, py: 5 }}>
              <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Inter, "Noto Sans", sans-serif' }}>
                <Box maxWidth="lg" mx="auto" py={4}>
                  <Box display="flex" px={2} mb={2}>
                    <Button variant="outlined" startIcon={<ArrowBack/>} onClick={()=>{navigate(-1)}}>Atrás</Button>
                    <Box display={"flex"} flexDirection={"column"} gap={2} ml={5}>
                      <Typography variant="h4" fontWeight={700} textAlign={"left"}>Cuadre de Caja</Typography>
                      <Typography variant="body1" color="text.secondary" textAlign={"left"} sx={{mt:-1}}>
                        Reporte detallado de ingresos y egresos de caja
                      </Typography>
                    </Box>
                  </Box>

                  {/* Filters Top Bar */}
                  <Paper sx={{ p: 3, mb: 3 }}>
                    {filterContent}
                  </Paper>

                  {/* Metric Cards */}
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    useFlexGap
                    sx={{ mb: 4, flexWrap: "wrap" }}
                  >
                    <MetricCard 
                      label="Total Ingresos" 
                      value={currency(totals.totalIncome)}
                      icon={<TrendingUpIcon sx={{ color: 'white', fontSize: 28 }} />}
                      delay={0}
                    />
                    <MetricCard 
                      label="Total Egresos" 
                      value={currency(totals.totalExpense)}
                      icon={<TrendingDownIcon sx={{ color: 'white', fontSize: 28 }} />}
                      delay={100}
                    />
                    <MetricCard 
                      label="Balance Neto" 
                      value={currency(totals.netBalance)}
                      icon={<AccountBalanceWalletIcon sx={{ color: 'white', fontSize: 28 }} />}
                      delay={200}
                      subtitle={totals.netBalance >= 0 ? 'Saldo positivo' : 'Saldo negativo'}
                    />
                    <MetricCard 
                      label="Transacciones" 
                      value={totals.transactions}
                      icon={<CheckCircleOutlineIcon sx={{ color: 'white', fontSize: 28 }} />}
                      delay={300}
                    />
                  </Stack>

                  {/* Data Grid */}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {filteredRows.length === 0 && !isLoadingData ? (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        py: 8,
                        textAlign: 'center'
                      }}>
                        <Typography variant="h6" fontWeight={600} color="text.primary" mb={1}>
                          No hay datos disponibles
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                          No se encontraron transacciones para los filtros seleccionados.
                        </Typography>
                        <Button 
                          variant="contained" 
                          onClick={handleGenerate}
                          color="primarybutton"
                        >
                          Aplicar Filtros
                        </Button>
                      </Box>
                    ) : (
                      <DataGrid
                        autoHeight
                        rows={filteredRows}
                        columns={columns}
                        minHeight={800}
                        disableRowSelectionOnClick
                        loading={isLoadingData}
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{
                          pagination: { paginationModel: { pageSize: 25 } },
                        }}
                        sx={{
                          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
                        }}
                      />
                    )}
                  </Paper>

                  {/* Totals footer */}
                  <Paper sx={{ mt: 3, p: 3, backgroundColor: '#f8f9fa' }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>
                          Total Capital
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="success.main">
                          {currency(totals.totalCapital)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>
                          Total Interés
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="success.main">
                          {currency(totals.totalInterest)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>
                          Total Mora
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="success.main">
                          {currency(totals.totalMora)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>
                          Total Ingresos
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="success.main">
                          {currency(totals.totalIncome)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>
                          Total Egresos
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="error.main">
                          {currency(totals.totalExpense)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>
                          Balance Neto
                        </Typography>
                        <Typography 
                          variant="h6" 
                          fontWeight={700} 
                          color={totals.netBalance >= 0 ? 'primary.main' : 'warning.main'}
                        >
                          {currency(totals.netBalance)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>
                          Total Transacciones
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {totals.transactions}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Category Breakdown */}
                  <Paper sx={{ mt: 2, p: 3, backgroundColor: 'white' }}>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                      Desglose por Categoría
                    </Typography>
                    <Grid container spacing={2}>
                      {transactionCategories.map((cat) => (
                        <Grid item xs={12} sm={6} md={3} key={cat.value}>
                          <Box 
                            sx={{ 
                              p: 2, 
                              borderRadius: 2, 
                              border: `2px solid`,
                              borderColor: `${cat.color}.main`,
                              backgroundColor: `${cat.color}.50`,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 2
                              }
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                              <Chip 
                                label={cat.label} 
                                color={cat.color} 
                                size="small"
                              />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" mb={0.5}>
                              Transacciones: {totals.byCategory[cat.value]?.count || 0}
                            </Typography>
                            <Typography variant="h6" fontWeight={700}>
                              {currency(totals.byCategory[cat.value]?.total || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Box>
              </Box>
            </Container>
          </Stack>
        </Box>
      </Box>

      {/* Alert Message Dialog */}
      <AlertMessageDialog
        open={alertOpen}
        setOpen={setAlertOpen}
        handleClose={handleCloseAlert}
        title={alertConfig.title}
        message={alertConfig.message}
        severity={alertConfig.severity}
      />
    </AppTheme>
  );
}

